import { useEffect, useMemo, useState } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router"
import { RoleType, SearchFilter, StorageKey } from "../../../App/enums"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import SessionService from "../../../App/SessionService"
import { RootState } from "../../../App/store"
import "../../../Styles/main.scss"
import NotificationsDropdown from "../../Notifications/NotificationsDropdown"
import { setUserNotifications } from "../../Notifications/NotificationSlice"
import { organizationNotificationGetRequest } from "../../Notifications/OrganizationNotificationServiceRequest"
import { userNotificationGetRequest } from "../../Notifications/UserNotificationServiceRequest"
import { getOrganizationData } from "../../Organization/OrganizationDashboard/GetOrganizationServiceRequest"
import { getUserNotificationsSettingsState, getUserPaymentSettingsState, getUserPrivacySettingsState, getUserProfileSettingsState } from "../../User/UserSettings/UserSettingsSlice"
import { BackIcon, ChatIcon, ChatIconActive, EdusiiaSmallIcon, HomeIcon, MediumLogoIcon } from "../Components/SvgIcons"
import TutorialBubble from "../Components/TutorialBubble"
import SearchInput from "../Primitive/SearchInput"
import { onGetGlobalSearchFilteredData } from "../Search/GlobalSearchServiceRequest"
import { setGlobalSearchData } from "../Search/GlobalSearchSlice"
import SearchDropdown from "../Search/SearchDropdown"
import { setModalData } from "../SharedSlices/ModalSlice"
import { setIsOwner, setOrganizationData } from "../SharedSlices/OrganizationsSlice"
import { setRoleStatusData } from "../SharedSlices/RolesSlice"
import HeaderBurgerMenu from "./HeaderBurgerMenu"
import HeaderHelpMenu from "./HeaderHelpMenu"
import HeaderMobileMenu from "./HeaderMobileMenu"
import { onDashboardNavigation } from "./HeaderNavigationFunctions"
import HeaderProfileMenu from "./HeaderProfileMenu"
import UpcomingEventsDropdown from "../../UpcomingEvents/UpcomingEventsDropdown"
import { setUpcomingEvents } from "../../UpcomingEvents/UpcomingEventsSlice"
import { upcomingOrganizationEventsGetRequest, upcomingUserEventsGetRequest } from "../../UpcomingEvents/UpcomingEventsServiceRequest"
import { HapticFeedback } from "../../../App/Helpers/HapticFeedback"
import MobileBottomNav from "../Layout/MobileBottomNav"

function Header(props: { roomHeader?: boolean }) {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation()
    const [isLoading, setIsLoading] = useState(true)
    const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false)
    const [isMobileSearchDropdownOpen, setIsMobileSearchDropdownOpen] = useState(false)
    const { id } = useParams()
    const orgId = SessionService.getItem(StorageKey.organizationId)
    const isAuthenticated = useSelector((state: RootState) => state.signinSlice.authenticationSlice.hasAccessToken)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    let params = new URLSearchParams(location.search)
    const searchedQuery = params.get("searchedQuery")
    const [searchParams, setSearchParams] = useSearchParams()
    let searchRef = React.useRef<HTMLInputElement>(null)
    let mobileSearchRef = React.useRef<HTMLInputElement>(null)
    const searchPagePathnames = useMemo(() => [`/search-screen/${id}`, `/search-screen`], [id])
    const hasUnseenMessages = useSelector((state: RootState) => state.chatSlice.hasUnseenMessages)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const translation = useSelector(selectTranslations)
    const [canNavigateBack, setCanNavigateBack] = useState(false)
    const initialMobileRouteRef = React.useRef(`${location.pathname}${location.search}${location.hash}`)
    const hideMobileBottomNav = useMemo(() => {
        const path = location.pathname
        return (
            /^\/room(\/|$)/.test(path) ||
            /^\/room-post(\/|$)/.test(path) ||
            /^\/room-preview(\/|$)/.test(path) ||
            /^\/f\//.test(path) ||
            /^\/e\//.test(path)
        )
    }, [location.pathname])

    // replace statt push: sonst erzeugt jeder Tastendruck einen History-Eintrag
    const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updated = new URLSearchParams(searchParams)
        updated.set("searchedQuery", event.target.value)
        setSearchParams(updated, { replace: true })
    }

    const onEnterHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const enter = "Enter"
        if (event.key === enter && searchedQuery !== null) {
            let query = searchedQuery.replace("#", "%23")
            switch (role) {
                case RoleType.USER:
                    setIsSearchDropdownOpen(false)
                    setIsMobileSearchDropdownOpen(false)
                    navigate(`/search-screen?searchedQuery=${query}`)
                    window.scrollTo(0, 0)
                    break
                case RoleType.ORGANIZATION:
                    if (orgId === id) {
                        navigate(`/search-screen/${id}?searchedQuery=${query}`)
                        window.scrollTo(0, 0)
                    } else {
                        navigate(`/search-screen/${orgId}?searchedQuery=${query}`)
                        window.scrollTo(0, 0)
                    }

                    break
                default:
                    break
            }
        }
    }

    useEffect(() => {
        if (id) {
            getOrganizationData(id!)
                .then(response => {
                    response.data.organization.tutorial_progress = response.data.tutorial_progress
                    if (response.data.is_owner) {
                        dispatch(setOrganizationData(response.data.organization))
                        dispatch(setIsOwner(response.data.is_owner))
                        SessionService.setItem(StorageKey.organizationId, id!)
                    }
                    if (response.data.is_admin) {
                        dispatch(setOrganizationData(response.data.organization))
                        organizationNotificationGetRequest(id!)
                            .then(response => {
                                if (response.status === 200) {
                                    dispatch(setUserNotifications(response.data))
                                }
                            })
                            .catch(error => {
                                if (error.response.status === 403) {
                                    navigate("/no-permission")
                                }
                            })

                            upcomingOrganizationEventsGetRequest(id!).then(response => {
                                if (response.status === 200) {
                                    dispatch(setUpcomingEvents(response.data.results))
                                }
                            })
                    }
                })
                .catch()
        } else {
            SessionService.setItem(StorageKey.roles, RoleType.USER)
        }
    }, [dispatch, id, navigate])

    useEffect(() => {
        if (isAuthenticated && role === RoleType.USER && !id) {
            userNotificationGetRequest().then(response => {
                if (response.status === 200) {
                    dispatch(setUserNotifications(response.data))
                }
            })

            upcomingUserEventsGetRequest().then(response => {
                if (response.status === 200) {
                    dispatch(setUpcomingEvents(response.data.results))
                }
            })
        }
    }, [id, dispatch, isAuthenticated, role])

    useEffect(() => {
        const currentRoute = `${location.pathname}${location.search}${location.hash}`
        const historyState = window.history.state as { idx?: number } | null
        const hasRouterHistory = typeof historyState?.idx === "number" && historyState.idx > 0
        const hasNavigatedInSession = currentRoute !== initialMobileRouteRef.current

        setCanNavigateBack(hasNavigatedInSession || hasRouterHistory)
    }, [location.pathname, location.search, location.hash])

    const onMobileLogoClick = () => {
        HapticFeedback.light()
        onDashboardNavigation(role, navigate, id!, location.pathname)
    }

    const onMobileBackClick = () => {
        HapticFeedback.light()
        const historyState = window.history.state as { idx?: number } | null
        const hasRouterHistory = typeof historyState?.idx === "number" && historyState.idx > 0

        if (hasRouterHistory || window.history.length > 1) {
            navigate(-1)
            return
        }

        onDashboardNavigation(role, navigate, id!, location.pathname)
    }

    useEffect(() => {
        const onMouseDownHandler = (event: Event) => {
            if (isSearchDropdownOpen && searchRef.current && !searchRef.current.contains(event.target as HTMLElement)) {
                setIsSearchDropdownOpen(false)
            }
            if (isMobileSearchDropdownOpen && mobileSearchRef.current && !mobileSearchRef.current.contains(event.target as HTMLElement)) {
                setIsMobileSearchDropdownOpen(false)
            }
        }
        document.addEventListener("mousedown", onMouseDownHandler)

        if (searchParams.has('settings')) {
            if (searchParams.get('settings') === 'notifications') {
                dispatch(setModalData({ open: true, settings: true }))
                dispatch(getUserNotificationsSettingsState(true))
                dispatch(getUserProfileSettingsState({ status: false, isHashtag: false }))
                dispatch(getUserPrivacySettingsState(false))
                dispatch(getUserPaymentSettingsState(false))
            } else if (searchParams.get('settings') === 'profile') {
                dispatch(setModalData({ open: true, settings: true }))
                dispatch(getUserNotificationsSettingsState(false))
                dispatch(getUserProfileSettingsState({ status: true, isHashtag: true }))
                dispatch(getUserPrivacySettingsState(false))
                dispatch(getUserPaymentSettingsState(false))
            }
        }
    })

    useEffect(() => {
        const getData = setTimeout(() => {
            if (searchedQuery !== null && searchedQuery!.length > 2 && !searchPagePathnames.includes(location.pathname)) {
                setIsSearchDropdownOpen(true)
                setIsMobileSearchDropdownOpen(true)
                setIsLoading(true)
                const hasHashtag = searchedQuery.charAt(0) === "#"
                onGetGlobalSearchFilteredData(searchedQuery.replace("#", "")!, 1, SearchFilter.all_results, hasHashtag)
                    .then(response => {
                        if (response.status === 200) {
                            dispatch(setGlobalSearchData({ result: response.data.results }))
                            setIsLoading(false)
                        }
                    })
                    .catch(error => {
                        console.log(error)
                    })
            } else if (searchedQuery !== null && searchedQuery?.length === 0) {
                setIsSearchDropdownOpen(false)
                setIsMobileSearchDropdownOpen(false)
                setIsLoading(true)
                dispatch(setGlobalSearchData({ result: [] }))
            } else if (searchedQuery === null) {
                setIsSearchDropdownOpen(false)
                setIsMobileSearchDropdownOpen(false)
                setIsLoading(true)
                dispatch(setGlobalSearchData({ result: [] }))
            }
        }, 500)

        return () => clearTimeout(getData)
    }, [dispatch, location.pathname, searchPagePathnames, searchedQuery])

    useEffect(() => {
        const unlockRoleStatus = setTimeout(() => {
            dispatch(setRoleStatusData(false))
        }, 5000)

        return () => clearTimeout(unlockRoleStatus)
    }, [dispatch])

    return (
        <nav className={`header ${props.roomHeader && `header--room-header`}`}>
            <div className="header__logo" onClick={() => onDashboardNavigation(role, navigate, id!, location.pathname)}>
                <MediumLogoIcon />
            </div>
            <div className="header__search-bar" data-tutorial-anchor="header-search" ref={searchRef}>
                {role === RoleType.USER && (
                    <TutorialBubble text={translation.tutorial_user_search} placement="bottom" visible={userDataState.tutorial_progress === 6} hasNext={true} routeOnNext="/dashboard" />
                )}
                <SearchInput
                    onRemoveIconClick={() => {
                        setSearchParams({ searchedQuery: "" }, { replace: true })
                    }}
                    searchvalue={searchedQuery || ""}
                    onKeyDown={onEnterHandler}
                    onChange={onSearchInputChange}
                    isGlobalSearch={true}
                />
                {isSearchDropdownOpen && <SearchDropdown isLoading={isLoading} />}
            </div>
            <div className="header__menu">
                <div className="header__menu-home" onClick={() => {
                    HapticFeedback.light();
                    onDashboardNavigation(role, navigate, id!, location.pathname);
                }}>
                    <div className="header__icon">
                        <HomeIcon />
                    </div>
                </div>

                {isAuthenticated && role === RoleType.USER && (
                    <div className="header__menu-chat" data-tutorial-anchor="header-chat" onClick={() => {
                        HapticFeedback.light();
                        navigate(`/chat`);
                    }}>
                        <div className="header__icon">
                            {hasUnseenMessages ?
                                (
                                    <ChatIconActive />
                                ) :
                                (
                                    <ChatIcon />
                                )}

                        </div>
                    </div>
                )}
                <UpcomingEventsDropdown />
                <NotificationsDropdown />
                <HeaderHelpMenu />
                <HeaderProfileMenu />
            </div>
            <div className={`header__mobile-left ${!canNavigateBack ? "header__mobile-left--no-back" : ""}`}>
                {canNavigateBack && (
                    <div className="header__mobile-back" onClick={onMobileBackClick}>
                        <BackIcon />
                    </div>
                )}
                <div className="header__mobile-logo" onClick={onMobileLogoClick}>
                    <EdusiiaSmallIcon />
                </div>
            </div>
            <div className="header__mobile-search" data-tutorial-anchor="header-search-mobile" ref={mobileSearchRef}>
                <SearchInput
                    onRemoveIconClick={() => {
                        setSearchParams({ searchedQuery: "" }, { replace: true })
                    }}
                    onKeyDown={onEnterHandler}
                    searchvalue={searchedQuery! || ""}
                    onChange={onSearchInputChange}
                    isGlobalSearch={true}
                />
                {isMobileSearchDropdownOpen && <SearchDropdown isLoading={isLoading} />}
            </div>
            <div className="header__mobile-menu">
                <HeaderMobileMenu />
                <HeaderBurgerMenu />
            </div>
            {isAuthenticated && !hideMobileBottomNav && <MobileBottomNav />}
        </nav>
    )
}

export default Header

